import { mapDTO } from '@twistezo/ts-dto-mapper'
import { isJSON } from 'validator'

import {
  Schema,
  SchemaAdUnitBidderParams,
  SchemaAdUnitBidderParamValidation,
  SchemaAdUnitBidderProps,
} from 'features/globalSetup/pages/Prebid/Bidders/EditBidderPage/pages/BidParamsAndConnection/form/schema'
import { ParamsKind } from 'features/globalSetup/pages/Prebid/Bidders/types'
import { AdUnitBidder } from 'features/globalSetup/types/adUnitBidder'
import { Primitive } from 'types/primitive'
import { parseValidJSON } from 'utils/json'
import { AdUnitBidderAttribute, Variables } from './types'

type From = Schema
type To = Variables

export const toDTO = (data: From, dirtyRowsIds?: AdUnitBidder['id'][]): To =>
  mapDTO<From, To>({ from: data }).transform((data: From): To => {
    const {
      adUnitBidders,
      domainIds: { domainBidderId },
      ...rest
    } = data

    const parsedAttributes: AdUnitBidderAttribute[] = attributesToDTO(adUnitBidders)

    let filteredByDirty: AdUnitBidderAttribute[] = []
    if (dirtyRowsIds !== undefined && dirtyRowsIds.length > 0) {
      filteredByDirty = parsedAttributes.filter(({ id }: AdUnitBidderAttribute) =>
        dirtyRowsIds.includes(id),
      )
    }

    return {
      adUnitBiddersAttributes: filteredByDirty,
      id: domainBidderId.value,
      ...rest,
    }
  })

const attributesToDTO = (adUnitBidders: Schema['adUnitBidders']): AdUnitBidderAttribute[] =>
  Object.entries(adUnitBidders).map(
    ([key, { _paramsValidation, csEnabled, csParams, s2sEnabled, s2sParams }]: [
      key: keyof Schema['adUnitBidders'],
      SchemaAdUnitBidderProps,
    ]): AdUnitBidderAttribute => ({
      csEnabled,
      csParams: paramsToDTO({
        _paramsValidation,
        kind: 'cs',
        params: csParams,
      }),
      id: key,
      s2sEnabled,
      s2sParams: paramsToDTO({
        _paramsValidation,
        kind: 's2s',
        params: s2sParams,
      }),
    }),
  )

const paramsToDTO = ({
  _paramsValidation,
  kind,
  params,
}: {
  _paramsValidation: SchemaAdUnitBidderParamValidation[]
  kind: ParamsKind
  params: SchemaAdUnitBidderParams
}): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]: [string, string | undefined]): [string, unknown] => {
      const rules: SchemaAdUnitBidderParamValidation | undefined = _paramsValidation.find(
        rule => rule.name === key && rule.kind === kind,
      )
      if (rules === undefined) {
        throw Error(`Validation rules not found for param ${key} of paramKind ${kind}.`)
      }
      const { type } = rules

      return [key, paramToDTO(value, type)]
    }),
  )

const paramToDTO = (value: string | undefined, type: Primitive): unknown => {
  if (value === undefined || value.length === 0) {
    return null
  }

  switch (type) {
    case 'float': {
      return Number(value.replace(',', '.'))
    }
    case 'integer': {
      return Number(value)
    }
    case 'string': {
      if (isJSON(value)) {
        return parseValidJSON(value)
      } else {
        return value
      }
    }
    default: {
      return value
    }
  }
}
